/* eslint-disable @typescript-eslint/no-unused-vars */
import { ChangeEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import MultiSelectInputBox from '../components/MultiSelectInputBox';
import Button from '../controls/Button';
import SidePanelContainer from '../controls/SidePanelContainer';
import {
  ActionData,
  AddDeviceV2Props,
  DeviceType,
  DeviceAttributes,
  Group
} from '../constants/telosair-interfaces';
import { addDeviceV2, editDevice } from '../services/dashboard-service';
import {
  deleteDevicePhoto,
  removeDeviceImage,
  resolveDeviceImage,
  uploadDevicePhoto
} from '../services/deviceImage.service';
import { TOAST_MESSAGES } from '../constants/global-constants';
import CustomInputBox from '../controls/CustomInputBox';
import CustomRadioButtonGroup from '../controls/CustomRadioButtonGroup';
import DeviceImageUpload, { PickedDeviceImage } from '../controls/DeviceImageUpload';

const options = [DeviceType.OUTDOOR, DeviceType.INDOOR];

type AddDeviceModalProps = {
  handleClose: (newDeviceAdded: boolean) => void;
  setIsGroupModalOpen: (state: boolean) => void;
  groups: Group[];
  actionData?: ActionData<DeviceAttributes>;
};
type FORMDATA = {
  deviceName: string;
  deviceType: DeviceType;
  serialNumber: string;
  purpose: string;
  type: string;
};
/**
 * Modal - AddDeviceModal.
 * @param props - Properties of AddDeviceModal.
 * @returns ReactElement.
 */
export default function AddDeviceModal(props: AddDeviceModalProps): ReactElement {
  const { handleClose, setIsGroupModalOpen, groups, actionData } = props;
  const [device, setDevice] = useState<DeviceAttributes>({} as DeviceAttributes);
  const [coordinates, setCoordinates] = useState('(40.78, -74.18)');
  const [chips, setChips] = useState<string[]>([]);
  const inputTagRef = useRef<any>(null);
  const [deviceLocationMode, setDeviceLocationMode] = useState('coordinates');
  const [loading, setLoading] = useState(false);
  // Preview URL shown in the control. `pickedImage` holds the bytes only when
  // the user has chosen a new file this session; an unchanged existing photo
  // has a preview but nothing to re-upload.
  const [deviceImage, setDeviceImage] = useState<string | null>(null);
  const [pickedImage, setPickedImage] = useState<PickedDeviceImage | null>(null);

  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: useMemo(() => {
      return {
        deviceName: actionData?.data?.deviceName || '',
        serialNumber: actionData?.data?.serialNumber?.toString() || '',
        purpose: actionData?.data?.purpose || '',
        deviceType: actionData?.data?.deviceType || DeviceType.INDOOR
      };
    }, [actionData])
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset({
      deviceName: actionData?.data?.deviceName || '',
      serialNumber: actionData?.data?.serialNumber?.toString() || '',
      purpose: actionData?.data?.purpose || '',
      deviceType: actionData?.data?.deviceType || DeviceType.INDOOR
    });
  }, [actionData]);

  /**
   * Sends the chosen image to the server so it is visible to everyone on the
   * account and to the mobile app, rather than living in this browser only.
   *
   * The device itself is already saved by the time this runs, so an image
   * failure warns instead of failing the whole save.
   * @param serialNumber - Device serial number.
   */
  const persistDeviceImage = async (serialNumber: number): Promise<void> => {
    if (Number.isNaN(serialNumber)) {
      return;
    }
    try {
      if (pickedImage?.blob) {
        await uploadDevicePhoto(serialNumber, pickedImage.blob);
        // Clear any legacy browser-local copy now that the server has it.
        removeDeviceImage(serialNumber);
      } else if (!deviceImage) {
        await deleteDevicePhoto(serialNumber);
        removeDeviceImage(serialNumber);
      }
    } catch {
      toast.warn('Device saved, but the image could not be uploaded.');
    }
  };

  /**
   * Function to create a new device.
   * @param formData - Form data.
   */
  const handleSaveDevice = async (formData: FORMDATA): Promise<any> => {
    const allChips = inputTagRef?.current?.forceAddChip();
    // if (formData.type === 'Outdoor') {
    //   allChips.push('Outdoor');
    // }
    try {
      setLoading(true);
      const serialNumber = parseInt(formData.serialNumber.toString(), 10);
      const data: AddDeviceV2Props = {
        id: device.id,
        serialNumber,
        purpose: formData.purpose,
        tags: allChips,
        deviceName: formData.deviceName,
        deviceType: formData.deviceType
      } as AddDeviceV2Props;

      if (actionData?.type === 'edit') {
        await editDevice(data, device.id);
        await persistDeviceImage(serialNumber);
        toast.success(TOAST_MESSAGES.edited);
        handleClose(true);
        setLoading(false);
        return;
      }
      await addDeviceV2(data);
      await persistDeviceImage(serialNumber);
      toast.success(TOAST_MESSAGES.added);
      setLoading(false);
      handleClose(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (actionData?.type === 'edit') {
      setDevice(actionData.data);
      setChips(actionData.data?.tags || []);
      setPickedImage(null);
      setDeviceImage(null);
      resolveDeviceImage(actionData.data?.serialNumber).then(setDeviceImage);
    } else {
      setPickedImage(null);
      setDeviceImage(null);
    }
  }, [actionData]);

  return (
    <SidePanelContainer closeModal={() => handleClose(false)}>
      <div className="pt-3 flex text-gray-500 dark:text-gray-300 text-lg">
        {actionData?.type === 'edit' ? 'Edit Device' : 'Add Device'}
      </div>
      <div className="pb-3 flex text-gray-500 dark:text-gray-500 text-sm">
        {actionData?.type === 'edit' ? '' : 'Add new devices to TelosAir'}
      </div>
      {/* eslint-disable-next-line */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSaveDevice)}>
          <CustomInputBox
            label="Device name"
            idname="deviceName"
            type="text"
            validationObject={{ required: 'Device name is required' }}
          />
          <CustomInputBox
            label="Device Number"
            idname="serialNumber"
            type="text"
            validationObject={{ required: 'Device number is required' }}
          />
          {/* <InputBox textValue={' '} setText={() => null} label="Gateway #" /> */}

          {/* Groups Dropdown */}
          {/* <Dropdown
        label="Group"
        name="group"
        options={groups.map((group) => ({ groupName: group.groupName }))}
        handleOptionsSelect={handleOptionsSelect}
        field="groupName"
      /> */}

          {/* CREATE GROUP BUTTON */}
          {/* <div className="my-8">
        <Button
          theme="secondary-gray"
          size="sm"
          name="Create Group"
          onClick={() => {
            setIsGroupModalOpen(true);
          }}
        />
      </div> */}

          {/* <Dropdown
        label="Configuration"
        name="config"
        options={configuration}
        handleOptionsSelect={handleOptionsSelect}
        field="configurationName"
      /> */}
          <CustomRadioButtonGroup<string>
            idName="deviceType"
            options={options.map((option) => ({
              id: option,
              name: option === 'OUTDOOR' ? 'Outdoor' : 'Indoor'
            }))}
            label="DeviceType"
          />
          <MultiSelectInputBox
            ref={inputTagRef}
            label="Tags"
            availableOptions={[]}
            chips={chips}
            setChips={setChips}
          />
          <CustomInputBox label="Purpose" idname="purpose" type="text" />

          <DeviceImageUpload
            value={deviceImage}
            onChange={(image) => {
              setPickedImage(image);
              setDeviceImage(image?.dataUrl ?? null);
            }}
          />

          <div
            style={{ width: 'inherit' }}
            className="p-5 fixed bottom-0 right-0 flex justify-center gap-3 bg-white dark:bg-container-600"
          >
            <div>
              <Button
                theme="secondary-color"
                size="sm"
                name="Discard"
                onClick={() => handleClose(false)}
                isBlockButton
              />
            </div>
            <Button
              theme="primary"
              size="sm"
              loading={loading}
              name="Save Changes"
              buttonType="submit"
              isBlockButton
            />
          </div>
        </form>
      </FormProvider>
    </SidePanelContainer>
  );
}
